import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';
import { Alert, notification } from 'antd';

import {
  selectors,
  fetchDevPlan,
  fetchDevPlans,
  postDevAttempt,
  purchaseNode,
} from 'state';
import {
  DevAsset,
  DevPlanActiveSession,
  DevPlanLaunchSelection,
  DevPlanState,
  DevRemediation,
  NodeStatus,
  PlanNode,
  TrackCode,
  VegaAttemptPayload,
} from './types';
import {
  buildPlanNodes,
  computeEdges,
  computeLayout,
  computeLayoutOcti,
  DEFAULT_WORLD,
  indexByCode,
  layoutModeFor,
  nodeStatus,
  octiWorld,
} from './helpers';
import useActivePlan from './useActivePlan';
import useDevPassAccess from './useDevPassAccess';
import useSoloLaunch from './useSoloLaunch';
import { useOctiVersion } from './octiV2/useOctiV2Access';
import OctiCanvasV2 from './octiV2/OctiCanvasV2';
import OctiCanvasV3 from './octiV3/OctiCanvasV3';
import ProgressHeader from './components/ProgressHeader';
import SkillTreeCanvas from './components/SkillTreeCanvas';
import NodeDetailPanel from './components/NodeDetailPanel';
import DevPlanPicker from './components/DevPlanPicker';
import ExistingSoloSessionModal from './components/ExistingSoloSessionModal';
import ActiveSessionsBanner from './components/ActiveSessionsBanner';
import AssetPlayerModal, {
  assetOrigin,
  PlayerRemediationItem,
  WorkspaceLadderOption,
} from './components/AssetPlayerModal';
import './developmentPlan.css';

/** Dev toggle for the experimental octi v2/v3 renderers. Hidden for now — v1 is
 *  the standard. Flip to `true` to show the v1|v2|v3 switch again; all v2/v3 code
 *  stays intact either way. */
const OCTI_TOGGLE_ENABLED = false as boolean;

/** Measure distance from viewport top and fill the rest — the page never scrolls
 *  (mirrors the AnalysisStudioLive fixed-viewport pattern). */
const useFillHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(480, window.innerHeight - top - 10));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return { ref, height };
};

/**
 * Inner view — receives the loaded plan and holds a local editable copy. The
 * invest/reset affordances are a mock stand-in for rank-up; Phase 5 replaces
 * them with attempt-driven progression posted to the backend.
 */
const DevelopmentPlanView: React.FC<{
  initialPlan: DevPlanState;
  planCode: string;
  onBack: () => void;
}> = ({ initialPlan, planCode, onBack }) => {
  const { ref, height } = useFillHeight();
  const dispatch = useDispatch();
  const reduxPlan = useSelector(selectors.devPlan);

  const [plan, setPlan] = useState<DevPlanState>(initialPlan);

  // Keep the local editable copy in sync with the store — so a /me refreshed by
  // an ingested attempt (Phase 5 progression) propagates to the tree. On mount
  // reduxPlan === initialPlan, so this is a no-op until an attempt updates it.
  useEffect(() => {
    if (reduxPlan) setPlan(reduxPlan);
  }, [reduxPlan]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Start with nothing selected → the detail panel's "Select a node" state. The
  // core's own detail lists every track, which duplicates the Tracks legend, so
  // don't auto-open it; clicking the core still shows it.
  const [coreSelected, setCoreSelected] = useState(false);
  const [focusTrack, setFocusTrack] = useState<TrackCode | null>(null);
  // Dev-only: swap the octi renderer for the experimental v2 (isolated). Only
  // consulted for octi plans; the four-track (cross) plans never see it.
  const [octiVer, setOctiVer] = useOctiVersion();
  // While the toggle is hidden, always render v1 regardless of any persisted
  // choice, so a previously-selected v2/v3 can't strand the user on it.
  const octiRenderer = OCTI_TOGGLE_ENABLED ? octiVer : 'v1';
  const [openAsset, setOpenAsset] = useState<DevAsset | null>(null);
  // Workspace mode (a rank that has a ladder): openAsset is the scenario pane, openLadder the
  // reference ladder pane. openLadder === null ⇒ single-iframe mode (games/drills/no-ladder rank).
  const [openLadder, setOpenLadder] = useState<DevAsset | null>(null);
  const [workspaceFocus, setWorkspaceFocus] = useState<'scenario' | 'ladder'>(
    'scenario',
  );
  // Failed-gate drills for the CURRENTLY-open scenario — rendered as the in-modal
  // "before you re-run" bar (option C). Cleared when a new asset opens; populated
  // when a fresh /attempt response carries routes for the open scenario's node.
  const [modalRemediation, setModalRemediation] = useState<DevRemediation[]>(
    [],
  );
  const [toast, setToast] = useState<{ text: string; key: number } | null>(
    null,
  );

  // Dev force-pass gate (§5.3): visit ?devPass=1 once to enable (persisted),
  // ?devPass=0 to clear. Never shown to real trainees — the anti-stuck mechanism
  // for test traversal. The param itself is captured by TraineeDashboard, which
  // mounts while the query string is still on the URL.
  const devForcePass = useDevPassAccess();

  // Progression mode — no longer a user-facing toggle. It rides the same dev flag
  // as force-pass: `?devPass=1` gives "open play" (any scenario regardless of
  // prereqs/level/XP), and everyone else gets locked progression, the real trainee
  // experience. The Games tab derives its own open-play state from the same flag,
  // so the two tabs stay in agreement (they used to share a localStorage key).
  const enforceLocks = !devForcePass;

  const flash = useCallback((text: string) => {
    setToast(t => ({ text, key: (t?.key ?? 0) + 1 }));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // Solo room (assessment-backed nodes): POST the launch, then follow the
  // stored intent into the session. The intent is cleared on consumption so a
  // later remount can't re-navigate.
  const history = useHistory();
  const {
    intent: launchIntent,
    error: launchError,
    preparing: launchPreparing,
    start: startSolo,
    clear: clearSolo,
  } = useSoloLaunch(planCode);
  const [launchingCode, setLaunchingCode] = useState<string | null>(null);
  const [blockedLaunch, setBlockedLaunch] = useState<{
    sessionId: number;
    selection: DevPlanLaunchSelection;
  } | null>(null);
  const launchSolo = useCallback(
    (node: PlanNode, blueprintSlug?: string, recommendationItemId?: number) => {
      setLaunchingCode(node.internal_code);
      startSolo({
        internalCode: node.internal_code,
        blueprint_slug: blueprintSlug,
        recommendation_item_id: recommendationItemId,
      });
    },
    [startSolo],
  );
  useEffect(() => {
    if (!launchingCode || !launchError) return;
    setLaunchingCode(null);
    notification.error({
      message: 'Scenario could not start',
      description: launchError,
    });
    dispatch(fetchDevPlan({ planCode }));
  }, [launchingCode, launchError, dispatch, planCode]);
  const purchase = useCallback(
    (node: PlanNode) => {
      dispatch(purchaseNode({ planCode, internalCode: node.internal_code }));
      flash(`Unlocking ${node.name}…`);
    },
    [dispatch, planCode, flash],
  );
  useEffect(() => {
    if (!launchIntent) return;
    clearSolo();
    setLaunchingCode(null);
    if (launchIntent.selection?.selection_honored === false) {
      setBlockedLaunch({
        sessionId: launchIntent.sessionId,
        selection: launchIntent.selection,
      });
      return;
    }
    if (launchIntent.resumed) {
      // Product terminology: say "single player session" in user-facing copy.
      // "Solo" remains an internal implementation term only.
      notification.info({
        message: 'Resuming your unfinished session',
        description:
          'Only one single player session can be in progress for this node, so we reopened it.',
      });
    }
    history.push(`/session/${launchIntent.sessionId}`);
  }, [launchIntent, clearSolo, history]);

  const openExistingSession = useCallback(() => {
    if (!blockedLaunch) return;
    const { sessionId } = blockedLaunch;
    setBlockedLaunch(null);
    history.push(`/session/${sessionId}`);
  }, [blockedLaunch, history]);

  const continueSession = useCallback(
    (session: DevPlanActiveSession) => {
      history.push(`/session/${session.session_id}`);
    },
    [history],
  );

  // Scorecard bridge: accept `vega:attempt` envelopes posted by an instrumented
  // asset iframe, origin-checked against the pm.api asset origin. Scoped to this
  // view and torn down on unmount (never a global/app-root listener — D6).
  useEffect(() => {
    const allowed = assetOrigin();
    const onMessage = (e: MessageEvent) => {
      if (allowed && e.origin !== allowed) return;
      const data = e.data as {
        type?: string;
        payload?: VegaAttemptPayload;
      } | null;
      if (!data || data.type !== 'vega:attempt' || !data.payload) return;
      dispatch(postDevAttempt({ ...data.payload, planCode }));
      flash('Result recorded');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [dispatch, flash, planCode]);

  // Opening a fresh asset clears the bar (no result yet for the new window).
  useEffect(() => {
    setModalRemediation([]);
  }, [openAsset]);

  // A fresh /attempt response replaces plan.remediation (new array ref). If the
  // modal is open on the scenario for that node, surface its failed-gate drills.
  // Guarded on the reference so a mere selection/open change never repopulates.
  const lastRemRef = useRef<DevRemediation[] | undefined>(plan.remediation);
  useEffect(() => {
    if (plan.remediation === lastRemRef.current) return;
    lastRemRef.current = plan.remediation;
    if (!openAsset) return;
    const node = plan.nodes.find(n => n.scenario_asset_key === openAsset.key);
    if (!node) return;
    const routes = (plan.remediation ?? []).filter(
      r => r.node_code === node.code,
    );
    if (routes.length) setModalRemediation(routes);
  }, [plan.remediation, plan.nodes, openAsset]);

  /* ---- Derived data (content × state merge at read) -------------------- */
  const nodes = useMemo(() => buildPlanNodes(plan), [plan]);
  const byCode = useMemo(() => indexByCode(nodes), [nodes]);
  // Only `/me` may mark a node/session in progress. A local click never creates
  // optimistic active state: new starts continue through launchNodeSession so
  // the backend remains authoritative for the one-session-per-node invariant.
  const activeSessions = useMemo(
    () => plan.active_sessions ?? [],
    [plan.active_sessions],
  );
  const activeNodeCodes = useMemo(() => {
    const codes = new Set<string>();
    activeSessions.forEach(session => {
      const node = nodes.find(
        candidate =>
          candidate.code === session.node_code ||
          candidate.internal_code === session.node_code,
      );
      if (node) codes.add(node.code);
    });
    return codes;
  }, [activeSessions, nodes]);
  const nodeNameByCode = useMemo(() => {
    const names: Record<string, string> = {};
    nodes.forEach(node => {
      // Backends have historically used both display and immutable node codes
      // at integration boundaries; accept either without guessing client-side.
      names[node.code] = node.name;
      names[node.internal_code] = node.name;
    });
    return names;
  }, [nodes]);
  // A plan with the four canonical tracks keeps the original cross layout; any
  // other track shape (e.g. the 11-track simple plan) falls back to octilinear.
  const layoutMode = useMemo(() => layoutModeFor(plan.tracks), [plan.tracks]);
  const positions = useMemo(
    () =>
      layoutMode === 'octi'
        ? computeLayoutOcti(nodes, plan.tracks)
        : computeLayout(nodes, plan.tracks),
    [layoutMode, nodes, plan.tracks],
  );
  const world = useMemo(
    () => (layoutMode === 'octi' ? octiWorld(positions) : DEFAULT_WORLD),
    [layoutMode, positions],
  );
  const edges = useMemo(() => computeEdges(nodes), [nodes]);

  const assetByKey = useMemo(() => {
    const map: Record<string, DevAsset> = {};
    plan.assets.forEach(a => {
      map[a.key] = a;
    });
    return map;
  }, [plan.assets]);

  // Resolve the failed-gate routes → ready-to-render drills (asset/node) for the bar.
  const modalRemediationItems: PlayerRemediationItem[] = useMemo(
    () =>
      modalRemediation.map(r => {
        const targetAsset = r.target_asset_key
          ? assetByKey[r.target_asset_key]
          : undefined;
        const targetNode = r.target_node_code
          ? byCode[r.target_node_code]
          : undefined;
        return {
          gate_key: r.gate_key,
          gate_name: r.gate_name,
          targetAsset,
          targetNodeCode: r.target_node_code ?? undefined,
          targetNodeName: targetNode?.name,
        };
      }),
    [modalRemediation, assetByKey, byCode],
  );

  // Reference-ladder options for the open scenario's node — drives the ladder-pane rank selector.
  const workspaceLadderOptions: WorkspaceLadderOption[] = useMemo(() => {
    if (!openAsset || !openLadder) return [];
    const node = nodes.find(n => n.scenario_asset_key === openAsset.key);
    if (!node) return [];
    return node.ladders
      .map(l => ({
        rank: l.rank,
        asset: assetByKey[l.asset_key],
        done: l.done,
      }))
      .filter((o): o is WorkspaceLadderOption => !!o.asset);
  }, [openAsset, openLadder, nodes, assetByKey]);

  // Node context (code/name/rank) for the workspace header + modal title.
  const workspaceContext = useMemo(() => {
    if (!openAsset || !openLadder) return null;
    const node = nodes.find(n => n.scenario_asset_key === openAsset.key);
    return node
      ? {
          code: node.code,
          name: node.name,
          rank: node.rank,
          maxRank: node.max_rank,
        }
      : null;
  }, [openAsset, openLadder, nodes]);

  const statusByCode = useMemo(() => {
    const map: Record<string, NodeStatus> = {};
    nodes.forEach(n => {
      map[n.code] = nodeStatus(n, byCode, plan);
    });
    return map;
  }, [nodes, byCode, plan.profile]);

  const trackColor = useMemo(() => {
    const map = {} as Record<TrackCode, string>;
    plan.tracks.forEach(t => {
      map[t.code] = t.color;
    });
    return map;
  }, [plan.tracks]);

  const counts = useMemo(() => {
    const map = {} as Record<TrackCode, { unlocked: number; total: number }>;
    plan.tracks.forEach(t => {
      map[t.code] = { unlocked: 0, total: 0 };
    });
    nodes.forEach(n => {
      map[n.track_code].total += 1;
      if (statusByCode[n.code]?.unlocked) map[n.track_code].unlocked += 1;
    });
    return map;
  }, [plan.tracks, nodes, statusByCode]);

  const unlockedCount = useMemo(
    () => nodes.filter(n => statusByCode[n.code]?.unlocked).length,
    [nodes, statusByCode],
  );

  // Mock recommendations: 0 whenever no admin-enabled recommendation exists for this
  // (trainee, tree), which is the default — the header chip then stays hidden.
  const recommendedCount = useMemo(
    () => nodes.filter(n => n.recommended).length,
    [nodes],
  );

  /* ---- Handlers -------------------------------------------------------- */
  const selectNode = useCallback((code: string) => {
    setSelectedId(code);
    setCoreSelected(false);
  }, []);

  const selectCore = useCallback(() => {
    setCoreSelected(true);
    setSelectedId(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId(null);
    setCoreSelected(false);
  }, []);

  // Dev force-pass (§5.3): post a synthetic PASSING attempt for the node's next
  // rank; the backend ranks it up + grants XP, and the refreshed /me flows back
  // through the store into the tree. The real anti-stuck mechanism for traversal.
  const forcePass = useCallback(
    (node: PlanNode) => {
      if (node.rank >= node.max_rank) {
        flash('Already maxed');
        return;
      }
      const nonce =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `force-${node.code}-${node.rank + 1}-${Date.now()}`;
      dispatch(
        postDevAttempt({
          kind: 'scenario',
          refId: node.code,
          rank: node.rank + 1,
          scorePct: 100,
          passed: true,
          durationMs: 0,
          clientNonce: nonce,
          detail: { forced: true, dev_force_pass: true },
          planCode,
        }),
      );
      flash(`Force-passed ${node.name} → rank ${node.rank + 1}`);
    },
    [dispatch, flash, planCode],
  );

  // The header's "Reset" button (re-fetch the tree, clear the selection, flash
  // "Refreshed") is hidden, so its handler is gone with it. The tree still
  // refreshes on mount and after every attempt — see the fetch effects above.

  // Open a single asset (game, or a remediation drill swap) in the iframe host — SINGLE mode,
  // so clear any workspace ladder. Placeholders (not built) stay a "not built yet" card.
  const pickAsset = useCallback(
    (asset: DevAsset) => {
      if (!asset.is_built) {
        flash(`“${asset.title}” isn’t built yet`);
        return;
      }
      setOpenAsset(asset);
      setOpenLadder(null);
    },
    [flash],
  );

  // Open a rank as a WORKSPACE: the node's scenario + its reference ladder (if any). `focus`
  // picks the initial split emphasis (scenario from Play, ladder from a Warm-up chip). The
  // reference ladder defaults to the next-incomplete rank (node.rank+1), or an explicit one.
  const openRank = useCallback(
    (
      node: PlanNode,
      opts?: { focus?: 'scenario' | 'ladder'; ladderKey?: string },
    ) => {
      const scenario = node.scenario_asset_key
        ? assetByKey[node.scenario_asset_key]
        : undefined;
      if (!scenario || !scenario.is_built) {
        flash(`“${scenario?.title ?? 'Scenario'}” isn’t built yet`);
        return;
      }
      const opts2 = node.ladders
        .map(l => ({ rank: l.rank, asset: assetByKey[l.asset_key] }))
        .filter(o => !!o.asset);
      const ref =
        (opts?.ladderKey && opts2.find(o => o.asset.key === opts?.ladderKey)) ||
        opts2.find(o => o.rank === node.rank + 1) ||
        opts2[0];
      setOpenAsset(scenario);
      setOpenLadder(ref?.asset ?? null);
      setWorkspaceFocus(opts?.focus ?? 'scenario');
    },
    [assetByKey, flash],
  );

  const selectLadder = useCallback((a: DevAsset) => setOpenLadder(a), []);

  const closePlayer = useCallback(() => {
    setOpenAsset(null);
    setOpenLadder(null);
  }, []);

  // From the in-modal remediation bar: close the window and jump to a node to revisit.
  const goToNode = useCallback(
    (code: string) => {
      setOpenAsset(null);
      setOpenLadder(null);
      selectNode(code);
    },
    [selectNode],
  );

  const selectedNode: PlanNode | null = selectedId
    ? (byCode[selectedId] ?? null)
    : null;
  const selectedStatus = selectedId ? (statusByCode[selectedId] ?? null) : null;

  return (
    <div ref={ref} className="devplan" style={{ height }}>
      <ProgressHeader
        profile={plan.profile}
        coins={plan.wallet?.coins}
        dailyGrant={plan.economy?.daily_grant}
        planName={plan.plan?.name}
        unlockedCount={unlockedCount}
        totalCount={nodes.length}
        recommendedCount={recommendedCount}
        onBack={onBack}
      />

      <ActiveSessionsBanner
        sessions={activeSessions}
        nodeNameByCode={nodeNameByCode}
        onContinue={continueSession}
      />

      {launchPreparing && (
        <Alert
          type="info"
          showIcon
          title="Preparing your scenario"
          description="Your session will open automatically when its assessment is ready."
        />
      )}

      <div className="devplan__body">
        {OCTI_TOGGLE_ENABLED && layoutMode === 'octi' && (
          <div className="devplan-octi-vtoggle" title="Octi renderer (dev)">
            {(['v1', 'v2', 'v3'] as const).map(v => (
              <button
                key={v}
                type="button"
                className={octiVer === v ? 'is-on' : ''}
                onClick={() => setOctiVer(v)}
              >
                {v}
              </button>
            ))}
          </div>
        )}
        {layoutMode === 'octi' && octiRenderer === 'v3' ? (
          <OctiCanvasV3
            nodes={nodes}
            tracks={plan.tracks}
            edges={edges}
            byCode={byCode}
            statusByCode={statusByCode}
            activeNodeCodes={activeNodeCodes}
            trackColor={trackColor as unknown as Record<string, string>}
            selectedId={selectedId}
            coreSelected={coreSelected}
            focusTrack={focusTrack}
            onFocus={setFocusTrack as unknown as (code: string | null) => void}
            onSelectNode={selectNode}
            onSelectCore={selectCore}
            onBackground={clearSelection}
          />
        ) : layoutMode === 'octi' && octiRenderer === 'v2' ? (
          <OctiCanvasV2
            nodes={nodes}
            tracks={plan.tracks}
            edges={edges}
            byCode={byCode}
            statusByCode={statusByCode}
            activeNodeCodes={activeNodeCodes}
            trackColor={trackColor as unknown as Record<string, string>}
            selectedId={selectedId}
            coreSelected={coreSelected}
            focusTrack={focusTrack}
            onFocus={setFocusTrack as unknown as (code: string | null) => void}
            onSelectNode={selectNode}
            onSelectCore={selectCore}
            onBackground={clearSelection}
          />
        ) : (
          <SkillTreeCanvas
            nodes={nodes}
            tracks={plan.tracks}
            byCode={byCode}
            positions={positions}
            edges={edges}
            statusByCode={statusByCode}
            activeNodeCodes={activeNodeCodes}
            trackColor={trackColor}
            counts={counts}
            selectedId={selectedId}
            coreSelected={coreSelected}
            focusTrack={focusTrack}
            onFocus={setFocusTrack}
            onSelectNode={selectNode}
            onSelectCore={selectCore}
            onBackground={clearSelection}
            world={world}
            layoutMode={layoutMode}
          />
        )}

        <aside className="devplan__side">
          <NodeDetailPanel
            node={selectedNode}
            coreSelected={coreSelected}
            state={plan}
            nodes={nodes}
            status={selectedStatus}
            byCode={byCode}
            trackColor={trackColor}
            devForcePass={devForcePass}
            enforceLocks={enforceLocks}
            onSelectNode={selectNode}
            onPickAsset={pickAsset}
            onOpenRank={openRank}
            onForcePass={forcePass}
            onLaunchSolo={launchSolo}
            onContinueSession={continueSession}
            onPurchase={purchase}
            launching={launchingCode !== null}
          />
        </aside>
      </div>

      {toast && (
        <div className="devplan-toast" key={toast.key}>
          {toast.text}
        </div>
      )}

      <AssetPlayerModal
        asset={openAsset}
        ladder={openLadder}
        workspaceFocus={workspaceFocus}
        ladderOptions={workspaceLadderOptions}
        context={workspaceContext}
        remediation={modalRemediationItems}
        onClose={closePlayer}
        onOpenDrill={pickAsset}
        onGoToNode={goToNode}
        onSelectLadder={selectLadder}
      />

      <ExistingSoloSessionModal
        selection={blockedLaunch?.selection ?? null}
        onOpenExisting={openExistingSession}
        onStay={() => setBlockedLaunch(null)}
      />
    </div>
  );
};

/**
 * Outer: always-on picker landing. With a tree selected, fetch /plans/<code>/me and
 * hand it to the view. If the active tree becomes unavailable (hidden/gone/404), drop
 * SILENTLY back to the picker — no error screen, no user-facing "unavailable" copy
 * (the "visible list" is an internal check).
 */
const DevelopmentPlan: React.FC = () => {
  const dispatch = useDispatch();
  const [activePlanCode, setActivePlan] = useActivePlan();
  const plans = useSelector(selectors.devPlans);
  const plansFetched = useSelector(selectors.devPlansFetched);
  const plan = useSelector(selectors.devPlan);
  const fetched = useSelector(selectors.devPlanFetched);
  const itemCode = useSelector(selectors.devPlanItemCode);
  const error = useSelector(selectors.devPlanError);

  // Always keep the visible plan list current (drives the picker + the fallback check).
  useEffect(() => {
    dispatch(fetchDevPlans({}));
  }, [dispatch]);

  // Load the active tree when one is selected.
  useEffect(() => {
    if (activePlanCode) dispatch(fetchDevPlan({ planCode: activePlanCode }));
  }, [dispatch, activePlanCode]);

  const visibleCodes = useMemo(() => new Set(plans.map(p => p.code)), [plans]);

  // Active tree is hidden/gone (not in the published list) OR its fetch errored →
  // silently fall back to the picker. `!!error && itemCode !== activePlanCode` catches
  // a 404 for the active tree without clobbering a successfully-loaded tree.
  const activeGone =
    !!activePlanCode &&
    ((plansFetched && !visibleCodes.has(activePlanCode)) ||
      (!!error && itemCode !== activePlanCode));

  useEffect(() => {
    if (activeGone) setActivePlan(null);
  }, [activeGone, setActivePlan]);

  if (!activePlanCode || activeGone) {
    return <DevPlanPicker onSelect={setActivePlan} />;
  }

  if (!fetched || !plan || itemCode !== activePlanCode) {
    return (
      <div className="devplan devplan--message">
        Loading your development plan…
      </div>
    );
  }

  return (
    <DevelopmentPlanView
      key={activePlanCode}
      initialPlan={plan}
      planCode={activePlanCode}
      onBack={() => setActivePlan(null)}
    />
  );
};

export default DevelopmentPlan;
